/**
 * Family & members → Family → Phone reminder times.
 * The owner sets, for the whole family, when the phone app reminds:
 * breakfast / lunch / dinner medicines, how much earlier "before meal" doses
 * ring, blood pressure checks (times and days) and the blood pressure levels
 * (High / Very high / Low; Normal is in between), and from when the phone
 * log starts (nothing is reminded or counted as missed before it).
 * Others can only see them.
 * Saved in family_settings as REMINDER_TIMES_KEY (owner-only in the database).
 */
import { useState } from 'react';
import { DEFAULT_FAMILY_TIMES, REMINDER_TIMES_KEY, formatTime12, parseFamilyTimes, parseLogStart, type BpLimits } from '@bhorosha/core';
import { fmtDayDMY } from '../ui/dates';
import type { BhStore } from '@bhorosha/data';
import { Notice } from '../ui/components';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "01/11/2026, 8:00 AM" for a saved log start, or ''. */
export function logStartText(v: string | null | undefined): string {
  const ls = parseLogStart(v);
  return ls ? `${fmtDayDMY(ls.date)}, ${formatTime12(ls.time)}` : '';
}

export function ReminderTimes({ store, isOwner }: { store: BhStore; isOwner: boolean }) {
  const saved = parseFamilyTimes(store.getItem(REMINDER_TIMES_KEY));
  const [dt, setDt] = useState(saved.dose_times);
  const [bpOn, setBpOn] = useState(saved.bp.enabled);
  const [bpTimes, setBpTimes] = useState<string[]>([...saved.bp.times, '', ''].slice(0, 3));
  const [days, setDays] = useState<number[]>(saved.bp.days);
  const [lim, setLim] = useState<BpLimits>(saved.bp.limits);
  const savedStart = parseLogStart(saved.log_start);
  const [startOn, setStartOn] = useState(!!savedStart);
  const [startDate, setStartDate] = useState(savedStart?.date || '');
  const [startTime, setStartTime] = useState(savedStart?.time || '08:00');
  const [msg, setMsg] = useState('');

  if (!isOwner) {
    return (
      <div className="bh-box bh-times">
        <h3 className="bh-h3" style={{ marginTop: 0 }}>📱 Phone reminder times</h3>
        <p className="bh-small">Breakfast {formatTime12(saved.dose_times.morning)} · Lunch {formatTime12(saved.dose_times.lunch)} · Dinner {formatTime12(saved.dose_times.dinner)}
          {' '}· "before meal" {saved.dose_times.before_meal_minutes} min earlier · Blood pressure {saved.bp.enabled && saved.bp.times.length ? saved.bp.times.map(formatTime12).join(', ') : 'off'}</p>
        <p className="bh-small">Phone log starts: {logStartText(saved.log_start) || 'not set (from the first day)'}</p>
        <p className="bh-small">Blood pressure levels: High from {saved.bp.limits.high_sys}/{saved.bp.limits.high_dia} · Very high from {saved.bp.limits.very_high_sys}/{saved.bp.limits.very_high_dia} · Low at or under {saved.bp.limits.low_sys}/{saved.bp.limits.low_dia}</p>
        <p className="bh-small bh-muted">Only the family owner can change these.</p>
      </div>
    );
  }

  function save() {
    const times = bpTimes.filter(Boolean).sort().filter((t, i, a) => a.indexOf(t) === i);
    const bad = !(lim.low_sys < lim.high_sys && lim.high_sys < lim.very_high_sys && lim.low_dia < lim.high_dia && lim.high_dia < lim.very_high_dia);
    if (bad) { setMsg(''); alert('Please check the blood pressure levels: Low must be under High, and High under Very high (for both numbers).'); return; }
    let log_start: string | null = null;
    if (startOn) {
      if (!startDate || !/^\d{2}:\d{2}$/.test(startTime)) { setMsg(''); alert('Please pick the date and time the phone log starts from (or untick it).'); return; }
      log_start = `${startDate}T${startTime}`;
    }
    const value = { dose_times: dt, log_start, bp: { enabled: bpOn, times, days: days.slice().sort(), limits: lim } };
    store.setItem(REMINDER_TIMES_KEY, JSON.stringify(value));
    setMsg('Saved. Phones pick up the new times the next time they sync (usually within a few minutes).');
  }
  function reset() {
    const d = DEFAULT_FAMILY_TIMES;
    setDt(d.dose_times); setStartOn(false); setBpOn(d.bp.enabled); setBpTimes([...d.bp.times, '', ''].slice(0, 3)); setDays([]); setLim(d.bp.limits);
  }

  return (
    <div className="bh-box bh-times">
      <h3 className="bh-h3" style={{ marginTop: 0 }}>📱 Phone reminder times (whole family)</h3>
      <p className="bh-small bh-muted">The Bhorosha phone app rings at these times for everyone in this family. Only you, the owner, can change them.</p>
      {msg && <Notice tone="ok">{msg}</Notice>}
      <div className="bh-logstart">
        <label className="bh-check" style={{ fontWeight: 600 }}><input type="checkbox" checked={startOn} onChange={e => { setStartOn(e.target.checked); if (e.target.checked && !startDate) setStartDate(new Date().toISOString().slice(0, 10)); }} /> Start the phone log from a set date and time</label>
        {startOn && <div className="bh-ps-grid" style={{ marginTop: 6 }}>
          <label>Date<input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} /></label>
          <label>Time<input type="time" value={startTime} onChange={e => setStartTime(e.target.value || startTime)} /></label>
        </div>}
        <p className="bh-small bh-muted" style={{ margin: '4px 0 12px' }}>{startOn && startDate
          ? `Nothing before ${logStartText(`${startDate}T${startTime}`)} is reminded on the phone or counted as missed in the Daily log. Doses already marked stay in the log. Buying (Purchase Calculator) is not affected.`
          : 'Not set: the phone log and Daily log count every dose from the start.'}</p>
      </div>
      <div className="bh-ps-grid">
        <label>Breakfast medicines<input type="time" value={dt.morning} onChange={e => setDt({ ...dt, morning: e.target.value || dt.morning })} /></label>
        <label>Lunch medicines<input type="time" value={dt.lunch} onChange={e => setDt({ ...dt, lunch: e.target.value || dt.lunch })} /></label>
        <label>Dinner medicines<input type="time" value={dt.dinner} onChange={e => setDt({ ...dt, dinner: e.target.value || dt.dinner })} /></label>
        <label>"Before meal" doses ring
          <select value={dt.before_meal_minutes} onChange={e => setDt({ ...dt, before_meal_minutes: Number(e.target.value) })}>
            {[0, 15, 30, 45, 60].map(m => <option key={m} value={m}>{m ? `${m} min earlier` : 'at the meal time'}</option>)}
          </select>
        </label>
      </div>
      <p className="bh-small bh-muted" style={{ margin: '6px 0 12px' }}>Injection reminders ring at the time set for each injection on the Medicine Reminder page. "Every few days" medicines ring at breakfast time.</p>
      <label className="bh-check" style={{ fontWeight: 600 }}><input type="checkbox" checked={bpOn} onChange={e => setBpOn(e.target.checked)} /> Remind to check blood pressure</label>
      {bpOn && <>
        <div className="bh-ps-grid" style={{ marginTop: 6 }}>
          {bpTimes.map((v, i) => (
            <label key={i}>{i === 0 ? 'Check at' : 'and at'}<input type="time" value={v} onChange={e => setBpTimes(bpTimes.map((x, j) => (j === i ? e.target.value : x)))} /></label>
          ))}
        </div>
        <div className="bh-ps-days">Days: {DAYS.map((d, i) => (
          <label key={d}><input type="checkbox" checked={days.includes(i)} onChange={e => setDays(e.target.checked ? [...days, i] : days.filter(x => x !== i))} /> {d}</label>
        ))} <span className="bh-small bh-muted">(none ticked = every day)</span></div>
      </>}
      <h4 style={{ margin: '14px 0 4px' }}>Blood pressure levels</h4>
      <p className="bh-small bh-muted" style={{ marginTop: 0 }}>The phone app shows each reading as Normal, A bit high, Very high or Low using these numbers (top / bottom). Normal is anything between Low and High. Ask your doctor.</p>
      <div className="bh-bp-lim">
        {([['High from', 'high_sys', 'high_dia'], ['Very high from', 'very_high_sys', 'very_high_dia'], ['Low at or under', 'low_sys', 'low_dia']] as const).map(([label, s, d]) => (
          <label key={s}><span>{label}</span>
            <span className="bh-bp-pair">
              <input type="number" min={40} max={300} value={lim[s]} onChange={e => setLim({ ...lim, [s]: Number(e.target.value) || 0 })} aria-label={`${label} (top)`} />
              <b>/</b>
              <input type="number" min={20} max={200} value={lim[d]} onChange={e => setLim({ ...lim, [d]: Number(e.target.value) || 0 })} aria-label={`${label} (bottom)`} />
            </span>
          </label>
        ))}
      </div>
      <p className="bh-small bh-muted">Normal: above {lim.low_sys}/{lim.low_dia} and below {lim.high_sys}/{lim.high_dia}.</p>
      <div className="bh-row">
        <button className="bh-btn bh-btn-primary" onClick={save}>Save times</button>
        <button className="bh-btn bh-btn-ghost" onClick={reset}>Back to defaults</button>
      </div>
    </div>
  );
}
